import { apiError } from "../../../../../../utils/formErrors";
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { useNavigate } from "react-router-dom";
import TopBar from "../../../../../../component/TopBar";
import DataTable from "../../../../../../component/DataTable";
import MainBox from "../../../../container/MainBox";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import { SearchInput, StatCard, StatusChip } from "../../../../../../component/listing";
import { getActiveLegalUnit } from "../../../../../../services/globalData";
import { getEmployeeNames } from '../../../../../../utils/getEmployeeNames';
import { getAllExpensePolicy, publishPolicy } from '../../../../../../services/expense';
import BtnSecondary from '../../../../../../component/SecondaryButton';
import { formatDate, formatDateByDay } from '../../../../../../utils/formatDate';
import { useNotify } from '../../../../../../component/NotificationProvider';
import { tokens } from '../../../../../../theme';

const formatLimits = (categories) => {
  const s = categories?.map((c) => c?.limits?.map((l) => `Max ${l?.limit} ${l?.frequency}`).join(', ')).filter(Boolean).join(', ');
  return s || 'No limit set';
};

const ExpensePolicyListing = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const legalUnit = getActiveLegalUnit();
      const res = await getAllExpensePolicy(legalUnit?._id);
      if (res?.success === false) throw new Error(res?.message || "Failed to load expense policies");
      const combined = await getEmployeeNames(res.data, (res.data || []).map((d) => d.createdBy), "createdBy");
      setData(Array.isArray(combined) ? combined.reverse() : []);
    } catch (err) {
      setError(true);
      notify.error(err?.message || "Failed to load expense policies");
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (policyId) => {
    try {
      const res = await publishPolicy(policyId);
      if (res?.success === false) throw apiError(res, "Could not publish");
      notify.success("Policy published");
      fetchData();
    } catch (err) {
      notify.error(err?.message || "Could not publish policy");
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((p) => (p.name || "").toLowerCase().includes(q) || (p.code || "").toLowerCase().includes(q));
  }, [data, search]);

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Expense Policy"} />

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: "1.5rem" }}>
        <StatCard label="Total expense policies" value={data.length} />
      </Stack>

      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: "1.25rem" }}>
          <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search policy name or code" />
          <BtnPrimary to="/app/expense-policy/new/setting" sx={{ borderRadius: "8px", whiteSpace: "nowrap" }}>
            + Create Expense Policy
          </BtnPrimary>
        </Stack>

        <DataTable
          rows={filtered}
          loading={loading}
          error={error}
          onRetry={fetchData}
          errorTitle="Couldn't load expense policies"
          empty={{
            title: data.length === 0 ? "No expense policies yet" : "No matches",
            description: data.length === 0
              ? "Create a policy to define expense categories and limits."
              : "Try a different search.",
          }}
          onRowClick={(p) => navigate("/app/expense-policy/edit", { state: { policyId: p._id } })}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={[
            { key: "code", label: "Code", render: (p) => p.code || "—" },
            { key: "name", label: "Policy Name", cellSx: { fontWeight: 600 }, render: (p) => p.name || "—" },
            {
              key: "validity",
              label: "Validity",
              cellSx: { color: tokens.textMuted },
              render: (p) => (p.startDate ? `${formatDate(p.startDate)} – ${formatDate(p.endDate)}` : "—"),
            },
            { key: "limit", label: "Limit", cellSx: { color: tokens.textMuted }, render: (p) => formatLimits(p?.policyCateogary) },
            {
              key: "creator",
              label: "Created By",
              render: (p) => (p?.employee ? `${p.employee.fname || ""} ${p.employee.lname || ""}`.trim() : "—"),
            },
            { key: "updated", label: "Last Modified", cellSx: { color: tokens.textMuted }, render: (p) => formatDateByDay(p.updatedAt) },
            {
              key: "status",
              label: "Status",
              // Publish acts on the row without opening it, so it must not let
              // the row's own click through.
              cellSx: { "& > *": { pointerEvents: "auto" } },
              render: (p) => (
                <Box onClick={(e) => e.stopPropagation()}>
                  {p.status === "draft" ? (
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <StatusChip label="Draft" tone="warning" />
                      <BtnSecondary onClick={() => handlePublish(p._id)} sx={{ borderRadius: "8px", fontSize: "0.8125rem" }}>
                        Publish
                      </BtnSecondary>
                    </Stack>
                  ) : (
                    <StatusChip
                      label={p.status === "inactive" ? "Inactive" : "Active"}
                      tone={p.status === "inactive" ? "muted" : "success"}
                    />
                  )}
                </Box>
              ),
            },
          ]}
        />
      </MainBox>
    </Box>
  );
};

export default ExpensePolicyListing;
